import React, { useCallback, useEffect, useState } from 'react';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import IconButton from '@mui/material/IconButton';
import { CheckIfFavorite, Favorite_Create, Favorite_Delete } from 'services/UsersService';
import { EntityType } from '../common/models/EntityType';

interface FavoriteButtonProps {
  entityId: EntityType;
  recordId: number;
  successHandler?: (message: string) => void;
  errorHandler?: (message: string) => void;
}

const FavoriteButton = ({
  entityId,
  recordId,
  successHandler,
  errorHandler,
}: FavoriteButtonProps): JSX.Element => {
  const [isFavorite, setIsFavorite] = useState<boolean>(false);

  const checkIfFavoriteInternal = useCallback(
    async (entityId: number, recordId: number): Promise<boolean> => {
      try {
        return await CheckIfFavorite(entityId, recordId);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler?.(error.message);
        } else {
          errorHandler?.('An unknown error occurred.');
        }
        return false; // Or handle the error as appropriate
      }
    },
    [errorHandler]
  );

  const insertFavorite = useCallback(
    async (entityId: number, recordId: number): Promise<void> => {
      try {
        const res = await Favorite_Create(entityId, recordId, errorHandler);
        if (res) successHandler && successHandler('Added new Favorite');
      } catch (error) {
        if (error instanceof Error) {
          errorHandler?.(error.message);
        } else {
          errorHandler?.('An unknown error occurred.');
        }
      }
    },
    [errorHandler]
  );

  const deleteFavorite = useCallback(
    async (entityId: number, recordId: number): Promise<void> => {
      try {
        const res = await Favorite_Delete(entityId, recordId, errorHandler);
        if (res) successHandler && successHandler('Favorite Removed');
      } catch (error) {
        if (error instanceof Error) {
          errorHandler?.(error.message);
        } else {
          errorHandler?.('An unknown error occurred.');
        }
      }
    },
    [Favorite_Delete]
  );

  useEffect(() => {
    const fetchInitialState = async () => {
      const initialIsFavorite = await checkIfFavoriteInternal(entityId, recordId);
      setIsFavorite(initialIsFavorite);
    };

    fetchInitialState();
  }, [entityId, recordId, checkIfFavoriteInternal]);

  const handleClick = async () => {
    if (isFavorite) {
      await deleteFavorite(entityId, recordId);
    } else {
      await insertFavorite(entityId, recordId);
    }
    // After insert/delete, re-check the favorite status to ensure accuracy
    const newIsFavorite = await checkIfFavoriteInternal(entityId, recordId);
    setIsFavorite(newIsFavorite); // Update the state based on the new status
  };

  return (
    <IconButton onClick={handleClick}>
      {isFavorite ? <FavoriteIcon color="primary" /> : <FavoriteBorderIcon color="primary" />}
    </IconButton>
  );
};

export default FavoriteButton;
